import { useState, useMemo, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { MapPin, ArrowLeft, Search, Check, Minus, ChevronUp, ChevronDown, AlertTriangle } from 'lucide-react';
import { getCustomerLocationById, getLocationClinicDetails, getCustomerById } from '../services/customerApi';
import type { LocationClinicDetail } from '../types/clientCoverage';
import type { CustomerLocation, Customer } from '../types/clientClinic';
import { withXmId } from '@shared/utils/xmId';
import './LocationCoverageDetailPage.css';

// Sort types
type SortDirection = 'asc' | 'desc' | null;
type SortConfig = { key: string; direction: SortDirection };

// Sortable Column Header (same pattern as ClientCoveragePage)
interface SortableHeaderProps {
  label: string;
  sortKey: string;
  sortConfig: SortConfig;
  onSort: (key: string) => void;
  className?: string;
}

function SortableHeader({ label, sortKey, sortConfig, onSort, className }: Readonly<SortableHeaderProps>) {
  const isActive = sortConfig.key === sortKey;
  return (
    <th className={className}>
      <div className="coverage-th-content">
        <div className="coverage-th-sort-row" onClick={() => onSort(sortKey)}>
          <span className="coverage-th-label">{label}</span>
          <span className={`coverage-sort-chevrons ${isActive ? 'active' : ''}`}>
            <ChevronUp size={10} className={`coverage-sort-icon ${isActive && sortConfig.direction === 'asc' ? 'active' : ''}`} />
            <ChevronDown size={10} className={`coverage-sort-icon ${isActive && sortConfig.direction === 'desc' ? 'active' : ''}`} />
          </span>
        </div>
      </div>
    </th>
  );
}

// Category names are now returned as comma-separated strings from the API

// Capability cell helper
function CapabilityCell({ value }: { value: boolean | null }) {
  if (value === true) {
    return <td className="loc-detail-capability-check"><Check size={12} /></td>;
  }
  return <td className="loc-detail-capability-na"><Minus size={10} /></td>;
}

// Boolean capability cell helper (for non-nullable booleans)
function BoolCell({ value }: { value: boolean }) {
  if (value) {
    return <td className="loc-detail-capability-check"><Check size={12} /></td>;
  }
  return <td className="loc-detail-capability-na"><Minus size={10} /></td>;
}

// Build full address string
function formatAddress(location: CustomerLocation): string {
  const parts: string[] = [];
  if (location.street) parts.push(location.street);
  if (location.suite) parts.push(location.suite);
  const cityStateZip: string[] = [];
  if (location.city) cityStateZip.push(location.city);
  if (location.state) cityStateZip.push(location.state);
  if (location.zip) cityStateZip.push(location.zip);
  if (cityStateZip.length > 0) parts.push(cityStateZip.join(', '));
  return parts.join(', ');
}

export default function LocationCoverageDetailPage() {
  const { locationId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const customerId = Number(searchParams.get('customerId'));
  const initialRadius = Number(searchParams.get('radiusMiles')) || 25;

  const [radiusMiles, setRadiusMiles] = useState(initialRadius);
  const [searchFilter, setSearchFilter] = useState('');
  const [showApprovedOnly, setShowApprovedOnly] = useState(false);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'distanceMiles', direction: 'asc' });

  // Fetch location details
  const { data: location } = useQuery<CustomerLocation>({
    queryKey: ['customer-location', locationId],
    queryFn: () => getCustomerLocationById(Number(locationId)),
    enabled: !!locationId,
  });

  // Fetch customer details
  const { data: customer } = useQuery<Customer>({
    queryKey: ['customer', customerId],
    queryFn: () => getCustomerById(customerId),
    enabled: !!customerId,
  });

  // Fetch clinic details for this location
  const { data: clinics, isLoading, error } = useQuery<LocationClinicDetail[]>({
    queryKey: ['location-clinics', locationId, customerId, radiusMiles],
    queryFn: () => getLocationClinicDetails(customerId, Number(locationId), radiusMiles),
    enabled: !!locationId && !!customerId,
  });

  const handleSort = useCallback((key: string) => {
    setSortConfig((prev) => {
      if (prev.key !== key) return { key, direction: 'asc' };
      if (prev.direction === 'asc') return { key, direction: 'desc' };
      return { key: '', direction: null };
    });
  }, []);

  // Client-side filtering and sorting
  const filteredClinics = useMemo(() => {
    if (!clinics) return [];
    let list = [...clinics];

    // Filter: approved only
    if (showApprovedOnly) {
      list = list.filter(c => c.isApproved);
    }

    // Filter: search by name or code
    if (searchFilter) {
      const q = searchFilter.toLowerCase();
      list = list.filter(c =>
        c.name.toLowerCase().includes(q) ||
        (c.code && c.code.toLowerCase().includes(q))
      );
    }

    // Sort
    if (sortConfig.key && sortConfig.direction) {
      list.sort((a, b) => {
        let cmp = 0;
        const key = sortConfig.key as keyof LocationClinicDetail;

        switch (key) {
          case 'name':
          case 'code':
          case 'city':
          case 'state':
          case 'phone':
          case 'accountStatus':
          case 'fitTestType': {
            const aVal = (a[key] ?? '') as string;
            const bVal = (b[key] ?? '') as string;
            cmp = aVal.localeCompare(bVal, undefined, { sensitivity: 'base' });
            break;
          }
          case 'distanceMiles': {
            cmp = (a.distanceMiles ?? 0) - (b.distanceMiles ?? 0);
            break;
          }
          case 'categoryNames': {
            const aVal2 = (a.categoryNames ?? '');
            const bVal2 = (b.categoryNames ?? '');
            cmp = aVal2.localeCompare(bVal2, undefined, { sensitivity: 'base' });
            break;
          }
          case 'isApproved':
          case 'isER':
          case 'isAfterHour': {
            cmp = (a[key] ? 1 : 0) - (b[key] ? 1 : 0);
            break;
          }
          case 'hasPAT':
          case 'hasXRay':
          case 'dotCertification': {
            const aNum = a[key] === true ? 1 : a[key] === false ? 0 : -1;
            const bNum = b[key] === true ? 1 : b[key] === false ? 0 : -1;
            cmp = aNum - bNum;
            break;
          }
          default:
            cmp = 0;
        }

        return sortConfig.direction === 'asc' ? cmp : -cmp;
      });
    }

    return list;
  }, [clinics, showApprovedOnly, searchFilter, sortConfig]);

  // Summary counts
  const approvedCount = clinics ? clinics.filter(c => c.isApproved).length : 0;
  const totalCount = clinics ? clinics.length : 0;

  return (
    <div className="loc-detail-page">
      {/* Breadcrumb */}
      <nav className="loc-detail-breadcrumb">
        <Link to="/client-coverage">Client Coverage</Link>
        <span className="breadcrumb-separator">&rsaquo;</span>
        <span>{customer?.name ?? 'Customer'}</span>
        <span className="breadcrumb-separator">&rsaquo;</span>
        <span className="breadcrumb-current">{location?.name ?? 'Location'}</span>
      </nav>

      {/* Back link */}
      <Link to="/client-coverage" className="loc-detail-back-link">
        <ArrowLeft size={16} />
        Back to Client Coverage
      </Link>

      {/* Location header card */}
      {location && (
        <div className="loc-detail-header">
          <div className="loc-detail-header-title">
            <MapPin size={24} />
            <h1>{location.name}</h1>
          </div>
          {customer && (
            <div className="loc-detail-header-customer">{withXmId(customer.name, customer.xmId)}</div>
          )}
          <div className="loc-detail-header-address">
            {formatAddress(location)}
          </div>
          {location.latitude != null && location.longitude != null ? (
            <div className="loc-detail-header-coords">
              {location.latitude.toFixed(6)}, {location.longitude.toFixed(6)}
            </div>
          ) : (
            <div className="loc-detail-geocode-warning">
              <AlertTriangle size={14} />
              <span>This location is not geocoded. Proximity search results may be unavailable.</span>
            </div>
          )}
        </div>
      )}

      {/* Controls bar: radius + summary */}
      <div className="loc-detail-controls">
        <div className="loc-detail-radius-group">
          <label htmlFor="loc-detail-radius-select">Radius</label>
          <select
            id="loc-detail-radius-select"
            className="loc-detail-radius-select"
            value={radiusMiles}
            onChange={(e) => setRadiusMiles(Number(e.target.value))}
            aria-label="Radius"
          >
            <option value={5}>5 mi</option>
            <option value={10}>10 mi</option>
            <option value={15}>15 mi</option>
            <option value={25}>25 mi</option>
            <option value={50}>50 mi</option>
            <option value={100}>100 mi</option>
          </select>
        </div>
        {clinics && !isLoading && (
          <div className="loc-detail-summary">
            <strong>{totalCount}</strong> clinic{totalCount !== 1 ? 's' : ''} found, <strong>{approvedCount}</strong> approved
          </div>
        )}
      </div>

      {/* Toolbar: search + filter toggle */}
      <div className="loc-detail-toolbar">
        <div className="loc-detail-search-wrapper">
          <Search size={16} className="search-icon" />
          <input
            type="text"
            placeholder="Search by clinic name or code..."
            aria-label="Search clinics"
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
          />
        </div>
        <div className="loc-detail-filter-toggle">
          <button
            type="button"
            className={!showApprovedOnly ? 'active' : ''}
            onClick={() => setShowApprovedOnly(false)}
          >
            All
          </button>
          <button
            type="button"
            className={showApprovedOnly ? 'active' : ''}
            onClick={() => setShowApprovedOnly(true)}
          >
            Approved Only
          </button>
        </div>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="loc-detail-loading">
          <div className="loading-spinner" />
          <p>Loading clinic data...</p>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="loc-detail-error">
          Failed to load clinic data. Please try again.
        </div>
      )}

      {/* Empty state */}
      {clinics && !isLoading && filteredClinics.length === 0 && (
        <div className="loc-detail-empty">
          <MapPin size={48} strokeWidth={1.5} />
          <h3>{searchFilter || showApprovedOnly ? 'No matching clinics found' : 'No clinics within radius'}</h3>
          <p>{searchFilter || showApprovedOnly ? 'Try adjusting your filters.' : 'Try increasing the search radius.'}</p>
        </div>
      )}

      {/* Clinic table */}
      {clinics && !isLoading && filteredClinics.length > 0 && (
        <div className="loc-detail-table-container">
          <table className="loc-detail-table">
            <thead>
              <tr>
                <SortableHeader label="Clinic Name" sortKey="name" sortConfig={sortConfig} onSort={handleSort} />
                <SortableHeader label="Code" sortKey="code" sortConfig={sortConfig} onSort={handleSort} />
                <SortableHeader label="Distance" sortKey="distanceMiles" sortConfig={sortConfig} onSort={handleSort} />
                <SortableHeader label="City" sortKey="city" sortConfig={sortConfig} onSort={handleSort} />
                <SortableHeader label="State" sortKey="state" sortConfig={sortConfig} onSort={handleSort} />
                <SortableHeader label="Phone" sortKey="phone" sortConfig={sortConfig} onSort={handleSort} />
                <SortableHeader label="Approved" sortKey="isApproved" sortConfig={sortConfig} onSort={handleSort} />
                <SortableHeader label="ER" sortKey="isER" sortConfig={sortConfig} onSort={handleSort} />
                <SortableHeader label="24H" sortKey="isAfterHour" sortConfig={sortConfig} onSort={handleSort} />
                <SortableHeader label="PAT" sortKey="hasPAT" sortConfig={sortConfig} onSort={handleSort} />
                <SortableHeader label="XRay" sortKey="hasXRay" sortConfig={sortConfig} onSort={handleSort} />
                <SortableHeader label="DOT" sortKey="dotCertification" sortConfig={sortConfig} onSort={handleSort} />
                <SortableHeader label="FitTest" sortKey="fitTestType" sortConfig={sortConfig} onSort={handleSort} />
                <SortableHeader label="Categories" sortKey="categoryNames" sortConfig={sortConfig} onSort={handleSort} />
              </tr>
            </thead>
            <tbody>
              {filteredClinics.map((clinic) => (
                <tr
                  key={clinic.clinicId}
                  className={`loc-detail-row-clickable ${clinic.isApproved ? 'loc-detail-row-approved' : ''}`}
                  onClick={() => navigate(`/clinics/${clinic.clinicId}`)}
                >
                  <td className="loc-detail-cell-name" title={clinic.name}>{clinic.name}</td>
                  <td>{clinic.code || '-'}</td>
                  <td className="loc-detail-cell-distance">{clinic.distanceMiles.toFixed(1)} mi</td>
                  <td>{clinic.city || '-'}</td>
                  <td>{clinic.state || '-'}</td>
                  <td>{clinic.phone || '-'}</td>
                  <td className="loc-detail-cell-approved">
                    {clinic.isApproved ? <Check size={14} /> : <Minus size={10} className="loc-detail-icon-muted" />}
                  </td>
                  <BoolCell value={clinic.isER} />
                  <BoolCell value={clinic.isAfterHour} />
                  <CapabilityCell value={clinic.hasPAT} />
                  <CapabilityCell value={clinic.hasXRay} />
                  <CapabilityCell value={clinic.dotCertification} />
                  <td className="loc-detail-capability-na">
                    {clinic.fitTestType || <Minus size={10} />}
                  </td>
                  <td>{clinic.categoryNames || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
